import { useNavigate } from 'react-router-dom'
import { openTelegramLink } from '@tma.js/sdk'
import { useTranslation } from '../../i18n'
import type { UserRelation, TrainingSet, TrainingProgram, TrainingProgramActivation } from '../../types/api'
import { USER_RELATION_STATUS } from '../../constants'
import { userApi, trainingApi } from '../../api'
import { useState } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useSubscription } from '../../hooks/useSubscription'
import { useAuth } from '../../contexts/AuthContext'
import { translateUserRelationStatus } from '../../utils/statusTranslations'
import { Avatar, Badge, Button, DashedAddButton, ProgramAssignmentCard, TrainingSetCard } from '../ui'
import { EditVirtualTraineeModal } from '../trainee/EditVirtualTraineeModal'
import AssignProgramSheet from '../training/AssignProgramSheet'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { ApiError } from '../../api/client'
import { captureEvent } from '../../posthog'

interface UserDetailProps {
  user: UserRelation
  trainingSets: TrainingSet[]
  coachPrograms?: TrainingProgram[]
  traineeAssignment?: TrainingProgramActivation | null
  onAssignmentChange?: (assignment: TrainingProgramActivation | null) => void
  onTrainingSetDeleted?: (setId: number) => void
  loading: boolean
  error: string | null
  userType: 'trainee' | 'coach'
  backPath: string
  userId: string
  onRetry: () => void
}

export function UserDetail({
  user,
  trainingSets,
  coachPrograms,
  traineeAssignment,
  onAssignmentChange,
  onTrainingSetDeleted,
  loading,
  error,
  userType,
  backPath,
  userId,
}: UserDetailProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { usage } = useSubscription()
  const { user: me, refreshUser } = useAuth()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showAssignSheet, setShowAssignSheet] = useState(false)
  const [assignSheetInitialProgramId, setAssignSheetInitialProgramId] = useState<number | undefined>(undefined)
  const [isDeassigning, setIsDeassigning] = useState(false)
  const [deletingSetId, setDeletingSetId] = useState<number | null>(null)

  const isTrainee = userType === 'trainee'
  const isPendingApproval = !isTrainee && user?.relationStatus === USER_RELATION_STATUS.PENDING_APPROVAL_BY_TRAINEE
  const isOverTraineeLimit = usage ? usage.traineeCount > usage.maxTrainees : false
  const canManageSets = isTrainee && user?.relationStatus === USER_RELATION_STATUS.ACTIVE

  const deleteTypeLabel = isTrainee ? t.userDetail.delete.typeClient : t.userDetail.delete.typeSpecialist

  const handleDelete = async () => {
    const confirmMessage = `${t.userDetail.delete.confirmTitle}\n\n${t.userDetail.delete.confirmMessage.replace('{{type}}', deleteTypeLabel)}\n\n${t.userDetail.delete.confirmDetails}`
    
    if (!window.confirm(confirmMessage)) {
      return
    }

    setIsDeleting(true)
    try {
      if (isTrainee) {
        await userApi.deleteTrainee(user.id)
      } else {
        await userApi.deleteCoach(user.id)
      }
      await refreshUser()
      
      // Navigate back to the list after successful deletion
      navigate(backPath)
    } catch (err) {
      console.error('Error deleting relationship:', err)
      alert(`${t.userDetail.delete.errorTitle.replace('{{type}}', deleteTypeLabel)}\n\n${err instanceof Error ? err.message : 'Unknown error'}\n\n${t.userDetail.delete.errorRetry}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAccept = async () => {
    const warning = me?.planCode === 'SOLO'
      ? (t.userDetail.invite.soloCancelWarning || '')
      : ''
    const confirmText = warning
      ? `${warning}\n\n${t.userDetail.invite.acceptConfirm || 'Accept this coach invitation?'}`
      : (t.userDetail.invite.acceptConfirm || 'Accept this coach invitation?')
    if (!window.confirm(confirmText)) {
      return
    }

    setIsAccepting(true)
    try {
      await userApi.acceptCoachInvite(user.id)
      captureEvent('coach_invite_accepted', { coach_id: user.id })
      await refreshUser()
      // Navigate back to the list after successful acceptance
      navigate(backPath)
    } catch (err) {
      console.error('Error accepting invitation:', err)
      alert(`${t.userDetail.invite.acceptError || 'Error accepting invitation'}\n\n${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsAccepting(false)
    }
  }

  const handleDecline = async () => {
    if (!window.confirm(t.userDetail.invite.declineConfirm || 'Decline this coach invitation?')) {
      return
    }

    setIsDeclining(true)
    try {
      await userApi.declineCoachInvite(user.id)
      captureEvent('coach_invite_declined', { coach_id: user.id })
      // Navigate back to the list after successful decline
      navigate(backPath)
    } catch (err) {
      console.error('Error declining invitation:', err)
      alert(`${t.userDetail.invite.declineError || 'Error declining invitation'}\n\n${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsDeclining(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  async function handleDeassign() {
    if (!traineeAssignment || !onAssignmentChange) return
    if (!window.confirm(t.trainingPrograms.assignProgram.deassignButton + '?')) return
    setIsDeassigning(true)
    try {
      await trainingProgramActivationApi.deassignFromTrainee(traineeAssignment.programId, user.id)
      onAssignmentChange(null)
    } catch (err) {
      console.error('Error removing assignment:', err)
      alert(t.trainingPrograms.assignProgram.error.deassignFailed)
    } finally {
      setIsDeassigning(false)
    }
  }

  async function handleDeleteTrainingSet(set: TrainingSet) {
    if (!window.confirm(t.trainingSetDetail.confirmations.delete)) return
    setDeletingSetId(set.id)
    try {
      await trainingApi.deleteTrainingSet(user.id, set.id)
      onTrainingSetDeleted?.(set.id)
    } catch (err) {
      console.error('Error deleting training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.deleteFailed)
    } finally {
      setDeletingSetId(null)
    }
  }

  return (
    <div className="p-5">
      {loading ? (
        <div className="text-center py-10 text-base opacity-70">{t.common.loading}</div>
      ) : error ? (
        <div className="text-center py-10 px-5">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold mb-2">{t.userDetail.error.title}</h3>
          <p className="mb-6" style={{ color: theme.text.muted }}>{error}</p>
          <button
            className="rounded-2xl px-6 py-3 cursor-pointer transition-opacity hover:opacity-80 font-semibold text-sm"
            style={{
              backgroundColor: `${theme.buttonColor}18`,
              border: `1px solid ${theme.buttonColor}40`,
              color: theme.accent.text,
            }}
            onClick={() => navigate(backPath)}
          >
            {t.common.back}
          </button>
        </div>
      ) : user ? (
        <>
          <header className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <Avatar
                photoUrl={user.photoUrl}
                name={user.firstName
                  ? `${user.firstName} ${user.lastName || ''}`.trim()
                  : user.username || 'Unknown'}
                size="lg"
                fallbackBg={theme.isDark ? 'rgba(0, 136, 204, 0.2)' : 'rgba(0, 136, 204, 0.15)'}
              />
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold leading-tight truncate">
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ''}`.trim()
                    : user.username || 'Unknown'}
                </h1>
                <div className="flex flex-wrap gap-1.5 items-center mt-1.5">
                  {user.username && (
                    <span className="text-sm" style={{ color: theme.text.muted }}>@{user.username}</span>
                  )}
                  {user.relationStatus && user.relationStatus !== USER_RELATION_STATUS.ACTIVE && (
                    <span
                      className="inline-block text-xs px-2 py-0.5 rounded-md font-medium"
                      style={{
                        backgroundColor: user.relationStatus?.includes('PENDING')
                          ? 'rgba(234, 179, 8, 0.2)'
                          : 'rgba(156, 163, 175, 0.2)',
                        color: theme.textColor,
                      }}
                    >
                      {translateUserRelationStatus(user.relationStatus, t)}
                    </span>
                  )}
                  {user.isVirtual && (
                    <Badge variant="brand" size="sm" className="rounded-md">
                      👤 {t.userDetail.virtualBadge}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {!isPendingApproval && !user.isVirtual && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (user.username) {
                    openTelegramLink(`https://t.me/${user.username}`)
                  } else {
                    alert(t.userDetail.openChatNoUsername)
                  }
                }}
                disabled={!user.username}
                className="w-full flex items-center justify-center gap-2"
                aria-label={user.username ? t.userDetail.openChat : (t.userDetail.openChatNoUsername ?? t.userDetail.openChat)}
                title={!user.username ? (t.userDetail.openChatNoUsername ?? 'No Telegram username') : undefined}
              >
                <span aria-hidden>💬</span>
                <span>{t.userDetail.openChat}</span>
              </Button>
            )}

            {/* Accept/Decline buttons for pending coach invitations */}
            {isPendingApproval && (
              <div className="flex gap-3">
                <Button
                  onClick={handleAccept}
                  disabled={isAccepting || isDeclining}
                  variant="success"
                  size="sm"
                  className="flex-1"
                >
                  {isAccepting ? `⏳ ${t.userDetail.invite.accepting || 'Accepting...'}` : `✓ ${t.userDetail.invite.accept || 'Accept'}`}
                </Button>
                <Button
                  onClick={handleDecline}
                  disabled={isAccepting || isDeclining}
                  variant="danger"
                  size="sm"
                  className="flex-1"
                >
                  {isDeclining ? `⏳ ${t.userDetail.invite.declining || 'Declining...'}` : `✗ ${t.userDetail.invite.decline || 'Decline'}`}
                </Button>
              </div>
            )}
          </header>

          {/* Training Program assignment (coach↔trainee) */}
          {(isTrainee ? coachPrograms !== undefined : traineeAssignment !== undefined) && (
            <section className="mb-6">
              <h2 className="text-xl font-bold mb-3">{t.userDetail.programs.title}</h2>
              {traineeAssignment ? (
                <ProgramAssignmentCard
                  activation={traineeAssignment}
                  canManage={isTrainee && user.relationStatus === USER_RELATION_STATUS.ACTIVE}
                  isSaved={traineeAssignment.programIsSaved !== false}
                  isDeassigning={isDeassigning}
                  onOpen={() => navigate(`/training-programs/${traineeAssignment.programId}`)}
                  onChangeProgram={isTrainee ? () => { setAssignSheetInitialProgramId(undefined); setShowAssignSheet(true) } : undefined}
                  onDeassign={isTrainee ? handleDeassign : undefined}
                />
              ) : isTrainee && user.relationStatus === USER_RELATION_STATUS.ACTIVE ? (
                <DashedAddButton
                  onClick={() => { setAssignSheetInitialProgramId(undefined); setShowAssignSheet(true) }}
                >
                  {t.userDetail.programs.assignButton}
                </DashedAddButton>
              ) : (
                <p className="text-sm text-center py-4" style={{ color: theme.text.muted }}>
                  {t.userDetail.programs.noAssignment}
                </p>
              )}
            </section>
          )}

          {/* Training Sets */}
          <section className="mb-6">
            <div className="mb-4">
              <h2 className="text-xl font-bold mb-3">{t.userDetail.trainingSets.title}</h2>
              {isTrainee && !trainingSets.some(set => set.status === 'ACTIVE') && (
                <DashedAddButton
                  disabled={user.relationStatus !== 'ACTIVE' || isOverTraineeLimit}
                  onClick={() => {
                    if (isOverTraineeLimit) {
                      alert(t.createTrainingSet.errors.traineeLimit ||
                        `You have ${usage?.traineeCount} trainees but your plan allows ${usage?.maxTrainees}. Please upgrade or remove some trainees to create training sets.`)
                      return
                    }
                    if (user.relationStatus === 'ACTIVE') {
                      navigate(`/trainee/${userId}/create-training-set`)
                    }
                  }}
                >
                  {t.userDetail.trainingSets.createNew}
                </DashedAddButton>
              )}
            </div>

            {isTrainee && isOverTraineeLimit && (
              <div
                className="mb-4 rounded-2xl p-4"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl shrink-0">⚠️</span>
                  <div>
                    <div className="text-sm font-semibold mb-0.5">{t.dashboard.overLimitWarning.title}</div>
                    <p className="text-xs" style={{ color: theme.text.muted }}>
                      {t.createTrainingSet.errors.traineeLimit}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isTrainee && user.relationStatus !== 'ACTIVE' && !isOverTraineeLimit && (
              <div
                className="mb-4 rounded-2xl p-4"
                style={{
                  backgroundColor: 'rgba(234, 179, 8, 0.12)',
                  border: '1px solid rgba(234, 179, 8, 0.3)',
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl shrink-0">ℹ️</span>
                  <div>
                    <div className="text-sm font-semibold mb-0.5">{t.userDetail.trainingSets.cannotCreate.title}</div>
                    <p className="text-xs" style={{ color: theme.text.muted }}>
                      {t.userDetail.trainingSets.cannotCreate.description.replace('{{status}}', user.relationStatus)}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {trainingSets.filter(set => set.status === 'DRAFT' || set.status === 'ACTIVE' || set.status === 'CANCELLED').length > 0 && (
              <div className="space-y-3">
                {trainingSets.filter(set => set.status === 'DRAFT' || set.status === 'ACTIVE' || set.status === 'CANCELLED').map((set) => (
                  <TrainingSetCard
                    key={set.id}
                    trainingSet={set}
                    userType={userType}
                    userId={userId}
                    variant="current"
                    canManage={canManageSets}
                    isDeleting={deletingSetId === set.id}
                    onDelete={() => handleDeleteTrainingSet(set)}
                    onClick={() => navigate(`/${userType}/${userId}/set/${set.id}`)}
                  />
                ))}
              </div>
            )}
          </section>

          {isTrainee && (
            <section className="mb-6">
              <button
                type="button"
                onClick={() => navigate(`/trainee/${userId}/history`)}
                className="w-full rounded-2xl px-4 py-4 flex items-center justify-between gap-3 text-left active:opacity-70"
                style={{
                  backgroundColor: theme.cardBg,
                  border: `1px solid ${theme.cardBorder}`,
                }}
              >
                <div>
                  <p className="text-base font-semibold" style={{ color: theme.text.primary }}>
                    {t.userDetail.openHistory}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                    {t.userDetail.openHistoryHint}
                  </p>
                </div>
                <span className="text-xl opacity-50 shrink-0" aria-hidden>→</span>
              </button>
            </section>
          )}

          {!isPendingApproval && (
            <div className="flex flex-col gap-3 pb-6">
              {isTrainee && user.isVirtual && (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleEdit}
                  disabled={isDeleting}
                >
                  {t.userDetail.edit.button}
                </Button>
              )}
              <Button
                type="button"
                variant="danger"
                size="lg"
                fullWidth
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting
                  ? t.userDetail.delete.deleting
                  : (isTrainee ? t.userDetail.delete.buttonClient : t.userDetail.delete.buttonSpecialist)}
              </Button>
            </div>
          )}

        </>
      ) : null}

      {showAssignSheet && coachPrograms && onAssignmentChange && (
        <AssignProgramSheet
          programs={coachPrograms}
          traineeId={user.id}
          initialProgramId={assignSheetInitialProgramId}
          onClose={() => setShowAssignSheet(false)}
          onAssigned={activation => {
            onAssignmentChange(activation)
            setShowAssignSheet(false)
          }}
        />
      )}

      {/* Edit Modal */}
      <EditVirtualTraineeModal
        user={user}
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        onSuccess={() => window.location.reload()}
      />
    </div>
  )
}
